import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { SaasTenantPicker } from './SaasTenantPicker.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface AnalyticsRow {
  readonly analyticsId: string;
  readonly saasTenantId: string;
  readonly metricCategory: string;
  readonly metricKey: string;
  readonly metricValue: string;
  readonly snapshotAt: string;
}

const CATEGORIES = ['ADOPTION', 'GROWTH', 'FEATURE_UTILIZATION', 'LICENSE_CONSUMPTION', 'USER_ACTIVITY', 'RETENTION', 'OPERATIONAL_KPI', 'PLATFORM_HEALTH'];
const PAGE = 25;

/**
 * M43 §27 Tenant Analytics Dashboard (§18 Analytics). Operators browse the
 * persisted analytics snapshot store (adoption, growth, feature utilisation,
 * license consumption, retention, KPIs, platform health) — the clean feed to
 * M37 BI. Viewing needs ANALYTICS_VIEW.
 */
export function TenantAnalyticsDashboard(): ReactNode {
  const { token } = useAuth();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<AnalyticsRow[]>([]);
  const [total, setTotal] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (categoryFilter) params.set('metricCategory', categoryFilter);
    if (tenantFilter.trim()) params.set('saasTenantId', tenantFilter.trim());
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: AnalyticsRow[]; total: number }>(`/v1/saas/analytics?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, categoryFilter, tenantFilter, page]);

  return (
    <main aria-label="tenant-analytics-dashboard">
      <h1>Tenant Analytics Dashboard</h1>

      <section aria-label="analytics-filters">
        <label>Category
          <select aria-label="filter-category" value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <SaasTenantPicker label="Tenant" value={tenantFilter} onPick={(id) => { setTenantFilter(id); setPage(0); }} testId="filter-tenant-id" />
      </section>

      {error && <p role="alert" aria-label="analytics-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="analytics-total">{total} snapshot(s).</p>
          <table aria-label="analytics-table">
            <thead><tr><th scope="col">Category</th><th scope="col">Metric</th><th scope="col">Value</th><th scope="col">At</th></tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={4}>No analytics snapshots.</td></tr>
              ) : rows.map((a) => (
                <tr key={a.analyticsId} data-analytics={a.analyticsId}>
                  <td aria-label={`analytics-category-${a.analyticsId}`}>{a.metricCategory}</td>
                  <td>{a.metricKey}</td>
                  <td aria-label={`analytics-value-${a.analyticsId}`}>{a.metricValue}</td>
                  <td>{at(a.snapshotAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="analytics-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
